// Pastille d'état réutilisée par les onglets Commandes et Factures.
// Les couleurs sont bâties sur les mêmes triplets que .devis-toast.

type Tone = 'ok' | 'warn' | 'danger' | 'muted' | 'info';

function Badge({ tone, children }: { tone: Tone; children: React.ReactNode }) {
  return <span className={`status-badge tone-${tone}`}>{children}</span>;
}

// État de facturation d'une commande (sale.order.invoice_status).
export function InvoiceStatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'invoiced':
      return <Badge tone="ok">Facturée</Badge>;
    case 'to invoice':
      return <Badge tone="info">À facturer</Badge>;
    case 'upselling':
      return <Badge tone="warn">Vente add.</Badge>;
    default:
      return <Badge tone="muted">Rien à facturer</Badge>;
  }
}

// État de livraison d'une commande (sale.order.delivery_status).
export function DeliveryStatusBadge({ status }: { status: string | false }) {
  switch (status) {
    case 'full':
      return <Badge tone="ok">Livrée</Badge>;
    case 'partial':
      return <Badge tone="warn">Partielle</Badge>;
    case 'pending':
      return <Badge tone="info">À livrer</Badge>;
    default:
      return <Badge tone="muted">—</Badge>;
  }
}

// État de réception d'une commande d'achat (purchase.order.receipt_status).
export function ReceiptStatusBadge({ status }: { status: string | false }) {
  switch (status) {
    case 'full':
      return <Badge tone="ok">Reçue</Badge>;
    case 'partial':
      return <Badge tone="warn">Partielle</Badge>;
    case 'pending':
      return <Badge tone="info">À recevoir</Badge>;
    default:
      return <Badge tone="muted">—</Badge>;
  }
}

// État de facturation d'une commande d'achat.
export function PurchaseInvoiceBadge({ status }: { status: string | false }) {
  switch (status) {
    case 'invoiced':
      return <Badge tone="ok">Facturée</Badge>;
    case 'to invoice':
      return <Badge tone="info">À facturer</Badge>;
    default:
      return <Badge tone="muted">Non facturée</Badge>;
  }
}

// État de réception d'une facture fournisseur (stock incrémenté ?).
export function ReceiptBadge({ receipt }: { receipt: string }) {
  switch (receipt) {
    case 'received':
      return <Badge tone="ok">Reçu ✓</Badge>;
    case 'partial':
      return <Badge tone="warn">Réception partielle</Badge>;
    case 'service':
      return <Badge tone="muted">Service (sans stock)</Badge>;
    default:
      return <Badge tone="danger">Marchandise non reçue</Badge>;
  }
}

// État de stock d'un produit.
export function StockStatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'rupture':
      return <Badge tone="danger">Rupture</Badge>;
    case 'below_min':
      return <Badge tone="warn">Sous mini</Badge>;
    case 'overstock':
      return <Badge tone="info">Surstock</Badge>;
    default:
      return <Badge tone="ok">OK</Badge>;
  }
}

// État d'une facture : brouillon / validée, avec le statut de paiement.
export function InvoiceBadge({
  state,
  payment,
  overdue,
}: {
  state: string;
  payment: string;
  overdue?: boolean;
}) {
  if (state === 'draft') return <Badge tone="warn">Brouillon</Badge>;
  if (state === 'posted') {
    if (payment === 'paid' || payment === 'in_payment' || payment === 'reversed')
      return <Badge tone="ok">Payée</Badge>;
    if (payment === 'partial') return <Badge tone="info">Partiellement payée</Badge>;
    if (overdue) return <Badge tone="danger">En retard</Badge>;
    return <Badge tone="info">À payer</Badge>;
  }
  return <Badge tone="muted">{state}</Badge>;
}
